import * as O from 'fp-ts/lib/Option';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {
  ValidTaxIdentificationNumber,
  ExistingTaxIdentificationNumber,
  TaxIdentificationNumber,
  ValidatingTaxIdentificationNumber,
  TaxIdentificationNumberWithNoAgentNumbers,
} from 'Users/models/User/Agent/TaxIdentificationNumber';
import {AgentNumber} from 'Users/models/User/Agent/AgentNumber';
import {UnvalidatedTaxIdentificationNumber} from 'Agent/Policies/models/TaxIdentificationNumber';

export class UnvalidatedAgencyAssociation {
  public readonly key = 'UnvalidatedAgencyAssociation';

  constructor(
    public readonly taxId: TaxIdentificationNumber,
    public readonly isAgencyAdministrator: boolean,
    public readonly agentNumbers: AgentNumber[]
  ) {}
}

export class InvalidAgencyAssociation {
  public readonly key = 'InvalidAgencyAssociation';

  constructor(
    public readonly taxId: TaxIdentificationNumber,
    public readonly isAgencyAdministrator: boolean,
    public readonly agentNumbers: AgentNumber[]
  ) {}
}

export class ValidAgencyAssociation {
  public static create(taxId: TaxIdentificationNumber, isAgencyAdministrator: boolean, agentNumbers: AgentNumber[]) {
    const validatedAgentNumbers = NEA.fromArray(agentNumbers);
    const isValidTaxId =
      taxId instanceof ValidTaxIdentificationNumber || taxId instanceof ExistingTaxIdentificationNumber;

    return (taxId instanceof ValidTaxIdentificationNumber || taxId instanceof ExistingTaxIdentificationNumber) &&
      O.isSome(validatedAgentNumbers)
      ? new ValidAgencyAssociation(taxId, isAgencyAdministrator, validatedAgentNumbers.value)
      : new InvalidAgencyAssociation(taxId, isValidTaxId && isAgencyAdministrator, isValidTaxId ? agentNumbers : []);
  }

  public readonly key = 'ValidAgencyAssociation';
  private constructor(
    public readonly taxId: ValidTaxIdentificationNumber | ExistingTaxIdentificationNumber,
    public readonly isAgencyAdministrator: boolean,
    public readonly agentNumbers: NEA.NonEmptyArray<AgentNumber>
  ) {}
}

export const loadTaxIdentificationNumber = (
  taxId: ValidTaxIdentificationNumber | TaxIdentificationNumberWithNoAgentNumbers
) => ({isAgencyAdministrator, agentNumbers}: AgencyAssociation) =>
  ValidAgencyAssociation.create(taxId, isAgencyAdministrator, agentNumbers);
export const changeTaxIdentificationNumber = (taxId: string) => ({
  isAgencyAdministrator,
  agentNumbers,
}: AgencyAssociation) =>
  ValidAgencyAssociation.create(ValidatingTaxIdentificationNumber.create(taxId), isAgencyAdministrator, agentNumbers);
export const changeIsAgencyAdministrator = (isAgencyAdministrator: boolean) => ({
  agentNumbers,
  taxId,
}: AgencyAssociation) => ValidAgencyAssociation.create(taxId, isAgencyAdministrator, agentNumbers);
export const addAgentNumber = (agentNumber: AgentNumber) => ({
  taxId,
  isAgencyAdministrator,
  agentNumbers,
}: AgencyAssociation) => ValidAgencyAssociation.create(taxId, isAgencyAdministrator, [...agentNumbers, agentNumber]);
export const removeAgentNumber = (agentNumber: AgentNumber) => ({
  taxId,
  isAgencyAdministrator,
  agentNumbers,
}: AgencyAssociation) =>
  ValidAgencyAssociation.create(
    taxId,
    isAgencyAdministrator,
    agentNumbers.filter(an => an.value !== agentNumber.value || an.isNewYork !== agentNumber.isNewYork)
  );

export type AgencyAssociation = ValidAgencyAssociation | InvalidAgencyAssociation | UnvalidatedAgencyAssociation;

export type AgencyAssociationOptions<TReturn> = ToMatchOptions<AgencyAssociation, 'key', TReturn>;

export const matchAgencyAssociation = <TReturn>(
  agencyAssociation: AgencyAssociation,
  options: AgencyAssociationOptions<TReturn>
): TReturn => {
  switch (agencyAssociation.key) {
    case 'InvalidAgencyAssociation':
      return options.InvalidAgencyAssociation(agencyAssociation);
    case 'UnvalidatedAgencyAssociation':
      return options.UnvalidatedAgencyAssociation(agencyAssociation);
    case 'ValidAgencyAssociation':
      return options.ValidAgencyAssociation(agencyAssociation);
  }
};

export const EmptyAgencyAssociation = new UnvalidatedAgencyAssociation(
  new UnvalidatedTaxIdentificationNumber(''),
  false,
  []
);
